import { createContext, useContext, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useActiveCategory, ChampionshipCategory } from "@/hooks/useActiveCategory";

interface PublicChampionshipContextValue {
  championship: any | null;
  championshipId: string | null;
  isLoading: boolean;
  slug: string;
  categories: ChampionshipCategory[];
  activeCategoryId: string | null;
  activeCategory: ChampionshipCategory | null;
  setActiveCategory: (id: string) => void;
  hasMultipleCategories: boolean;
}

const PublicChampionshipContext = createContext<PublicChampionshipContextValue>({
  championship: null,
  championshipId: null,
  isLoading: true,
  slug: "",
  categories: [],
  activeCategoryId: null,
  activeCategory: null,
  setActiveCategory: () => {},
  hasMultipleCategories: false,
});

export function usePublicChampionship() {
  return useContext(PublicChampionshipContext);
}

export function PublicChampionshipProvider({ slug, children }: { slug: string; children: ReactNode }) {
  const { data: championship, isLoading } = useQuery({
    queryKey: ["public-championship", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("championships")
        .select("*")
        .eq("public_slug", slug)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!slug,
  });

  const cat = useActiveCategory(championship?.id || null);

  return (
    <PublicChampionshipContext.Provider
      value={{
        championship: championship || null,
        championshipId: championship?.id || null,
        isLoading,
        slug,
        categories: cat.categories,
        activeCategoryId: cat.activeCategoryId,
        activeCategory: cat.activeCategory,
        setActiveCategory: cat.setActiveCategory,
        hasMultipleCategories: cat.hasMultiple,
      }}
    >
      {children}
    </PublicChampionshipContext.Provider>
  );
}
